import React, { useState, useEffect, useRef } from 'react';
import yted from '@lf/api-client/lfApi';
import Speech from './Speech';
import AnimatingDot from '../../components/AnimatingDot';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import FeedbackButton from '../../components/FeedbackButton';
import { stream_post } from '@lf/api-client';
import { processText, parseLines } from '@lf/api-client/stream';
import { deleteArrayElement } from '@lf/api-client';
const _checkAnswer = async (qna, userAnswer, multipleChoice, nonce = 0, isRegen = false) => {
  try {
    return await stream_post(isRegen ? '/chat/regen' : '/chat/', {
      chat_id: qna.chat_id,
      user_response: userAnswer,
      multiple_choice: multipleChoice,
      nonce: nonce,
    });
  } catch (error) {
    console.error('Error chatting:', error);
    return '';
  }
};

const _streamChatResponse = async (qna) => {
  try {
    return await stream_post('/chat/speech', {
      chat_id: qna.chat_id,
    });
  } catch (error) {
    console.error('Error streaming chat response:', error);
    return '';
  }
};

const _appendHint = async (qna, hint) => {
  try {
    return await yted.post('/chat/hint', {
      chat_id: qna.chat_id,
      hint: hint,
    });
  } catch (error) {
    console.error('Error appending hint:', error);
    return '';
  }
};

const VideoQnA = ({ videoId, qna, onEoc }) => {
  const [hintIndex, setHintIndex] = useState(0);
  const [isChoicesShown, setIsChoicesShown] = useState(false);
  const [choices, setChoices] = useState(qna.multiple_choices);
  const [regenNonce, setRegenNonce] = useState(0);

  const [userAnswer, setUserAnswer] = useState('');
  const [chatHistory, setChatHistory] = useState(
    qna.messages.map((message) => ({ sender: message.role, text: message.message })));
  const [isAssistantLoading, setIsAssistantLoading] = useState(false);

  const [eoc, setEoc] = useState(qna.eoc);
  const eocTimeoutIdRef = useRef(null);

  function clearEocTimeoutId() {
    if (eocTimeoutIdRef.current) {
      clearTimeout(eocTimeoutIdRef.current);
      eocTimeoutIdRef.current = null;
    }
  }

  async function processAssistant(answer, isMultipleChoice, newChatHistory, isRegen = false) {
    setIsAssistantLoading(true);
    clearEocTimeoutId();
    
    // Update chat history with loading state
    setChatHistory([...newChatHistory, { sender: 'assistant', text: 'LOADING' }]);
    
    let isEoc = false;
    let starCount = 0;
    
    // Use the nonce for regeneration and increment it if it's a regeneration
    const currentNonce = isRegen ? regenNonce : 0;
    if (isRegen) {
      setRegenNonce(prevNonce => (prevNonce + 1) % 5);
    }
    
    const result = await _checkAnswer(qna, answer, isMultipleChoice, currentNonce, isRegen);
    await processText(result, (text) => {
      const lines = parseLines(text);
      if (lines[lines.length - 1].startsWith('EOM')) {
        starCount = parseInt(lines[lines.length - 1].split(' ')[1]);
        isEoc = true;
      } else {
        setChatHistory([...newChatHistory, { sender: 'assistant', text: text }]);
      }
    });
    
    setIsAssistantLoading(false);
    
    if (isEoc) {
      setEoc(true);
      onEoc(true, starCount, true);
    } else {
      // Reset EOC mark before setting the timeout handler. 
      // Upon timeout, EOC will be set back to true
      onEoc(false, 0, false);
      eocTimeoutIdRef.current = setTimeout(() => {
        onEoc(true, 0, false);
      }, 10000);
    }
    
    return { isEoc, starCount };
  }

  async function regenerateResponse(isReported) {
    if (!isReported)
      return;

    if (isAssistantLoading || chatHistory.length < 2)
      return;
      
    // Get the last user message
    const lastUserMessageIndex = [...chatHistory].reverse().findIndex(msg => msg.sender === 'user');
    if (lastUserMessageIndex === -1) return;
    
    const lastUserMessage = chatHistory[chatHistory.length - 1 - lastUserMessageIndex];
    
    // Remove the last assistant message
    const newChatHistory = chatHistory.slice(0, -1);
    
    // Check if we've reached the regeneration limit
    if (regenNonce >= 5) {
      setChatHistory([...newChatHistory, { sender: 'assistant', text: 'Maximum regeneration limit reached.' }]);
      return;
    }
    
    // Process the assistant response with regeneration flag
    await processAssistant(lastUserMessage.text, isChoicesShown, newChatHistory, true);
  }

  async function processAnswer(answer) {
    if (answer.trim() === '' || isAssistantLoading)
      return;

    const newChatHistory = [...chatHistory, { sender: 'user', text: answer }];
    setUserAnswer('');
    
    // Reset the regeneration nonce for new answers
    setRegenNonce(0);
    
    await processAssistant(answer, isChoicesShown, newChatHistory);
  }
  
  async function handleShowHint() {
    clearEocTimeoutId();
    
    const hint = qna.hints[hintIndex];
    setChatHistory([...chatHistory, { sender: 'hint', text: hint }]);
    setHintIndex(hintIndex + 1);

    await _appendHint(qna, hint);
  }

  function handleShowChoices() {
    setIsChoicesShown(true);
  }

  async function handleChoice(index) {
    await processAnswer(choices[index]);

    setChoices(deleteArrayElement(choices, index));
  }
  
  function handleAnswerChange(event) {
    clearEocTimeoutId();

    if (event.target.value.indexOf("\n") >= 0) {
      handleSubmit(event);
    }
    else {
      setUserAnswer(event.target.value);
    }
  }

  async function handleSubmit(event) {
    if (event)
      event.preventDefault();

    processAnswer(userAnswer);
  }

  useEffect(() => {
    return () => {
      if (eocTimeoutIdRef.current) {
        clearTimeout(eocTimeoutIdRef.current);
      }
    };
  }, []);

  return (
    <div>
      {chatHistory.map((message, index) => (
        <div key={index} className="mb-3">
        {
          ['assistant', 'hint'].includes(message.sender) && <div className="flex w-full">
            {
              message.text === 'LOADING' 
              ? <div className="py-1.5">
                  <AnimatingDot />
                </div>
              : <div className="w-full">
                  {index === 0 && message.sender === 'assistant' ? (
                    <h3 className="text-xl font-semibold text-darkrock leading-snug">
                      <MarkdownRenderer text={message.text} />
                      {
                        index === chatHistory.length - 1 && isAssistantLoading && <span className="ml-1">
                          <AnimatingDot />
                        </span>
                      }
                      {
                        index === chatHistory.length - 1 && !isAssistantLoading &&
                        <Speech getSpeech={async () => _streamChatResponse(qna)} />
                      }
                    </h3>
                  ) : (
                    <div className="inline-block p-2 border border-gray-300 rounded-xl bg-[#faf9f6]">
                      <MarkdownRenderer text={message.text} />
                      {
                        index === chatHistory.length - 1 && isAssistantLoading && <span className="ml-1">
                          <AnimatingDot />
                        </span>
                      }
                      {
                        index === chatHistory.length - 1 && !isAssistantLoading &&
                        <Speech getSpeech={async () => _streamChatResponse(qna)} />
                      }
                    </div>
                  )}
                </div>
            }
            {
              index === chatHistory.length - 1 && !isAssistantLoading && 
              <div className="flex flex-col justify-end min-w-8 py-1">
                <FeedbackButton contentIds={[qna.chat_id]} data={{chat_index: index, content: message.text}} afterFeedback={regenerateResponse} />
              </div>
            }
          </div>
        }
        {
          message.sender === 'user' && 
          <div className="w-4/5 ml-auto border border-pastelgreen p-2 rounded-xl bg-pastelgreen-light">
            <MarkdownRenderer text={message.text} />
          </div>
        }
        </div>
      ))}

      {
        !eoc && isChoicesShown && !isAssistantLoading && choices.map((choice, index) => (
          <div key={index} className="my-3 ml-8">
            <div onClick={() => handleChoice(index)} 
                className="inline-block cursor-pointer border border-pastelgreen bg-white rounded-lg py-1 px-2">
              {choice}
            </div>
          </div>
        ))
      }

      <div className="flex justify-end">
      {
        !eoc && hintIndex < 2 && <button onClick={handleShowHint} className="text-sm">Show hint</button>
      }
      {
        !eoc && !isChoicesShown && <button onClick={handleShowChoices} className="text-sm ml-4">Show choices</button>
      }
      </div>

      {
        !eoc && <form onSubmit={handleSubmit} className="m-1 mt-4">
          <textarea
            type="text"
            placeholder="Enter your answer here"
            maxLength={300}
            rows={2}
            value={userAnswer}
            onChange={handleAnswerChange}
            className="w-full p-1 border border-gray-300"
          />
        </form>
      }
    </div>
  );
}

export default VideoQnA;
